<template>
  <div class="utm-sharing-section d-flex justify-content-center">
    <div class="utm-sharing-content">
      <!-- Collapsible UTM Sharing -->
      <b-button v-b-toggle.collapse-utm-sharing class="m-1 light-grey-button" size="sm">
        {{ $t('modals.showPublishSuccess.shareWithTracking') }}
      </b-button>
      <b-collapse id="collapse-utm-sharing">
        <div class="pt-3">
          <!-- Campaign Name Input -->
          <b-form-group
            :label="$t('modals.showPublishSuccess.campaignName')"
            label-size="sm"
            class="mb-2 text-left campaign-input"
          >
            <b-form-input
              v-model="campaignName"
              size="sm"
              :placeholder="$t('modals.showPublishSuccess.campaignPlaceholder')"
            ></b-form-input>
          </b-form-group>

          <!-- Platform Buttons - 3 per row -->
          <div class="platform-buttons">
            <b-button
              v-for="platform in platforms"
              :key="platform.id"
              :variant="copiedType === platform.id ? 'success' : 'outline-secondary'"
              class="platform-btn"
              size="sm"
              @click="copyWithUtm(platform)"
              v-b-tooltip.hover
              :title="$t('modals.showPublishSuccess.copyFor', { platform: platform.name })"
            >
              <b-icon :icon="platform.icon" class="mr-1"></b-icon>
              <span class="platform-name">{{ platform.name }}</span>
              <b-icon v-if="copiedType === platform.id" icon="check" class="ml-1"></b-icon>
            </b-button>
          </div>

          <!-- Custom UTM Section (Collapsible) -->
          <b-button
            v-b-toggle.collapse-custom-utm
            variant="link"
            size="sm"
            class="mt-2"
          >
            <b-icon icon="gear"></b-icon>
            {{ $t('modals.showPublishSuccess.customUtm') }}
          </b-button>
          <b-collapse id="collapse-custom-utm">
            <div class="custom-utm-form mt-2 text-left">
              <b-form-group label="utm_source" label-size="sm" class="mb-2">
                <b-form-input v-model="customUtm.source" size="sm" placeholder="e.g., newsletter"></b-form-input>
              </b-form-group>
              <b-form-group label="utm_medium" label-size="sm" class="mb-2">
                <b-form-input v-model="customUtm.medium" size="sm" placeholder="e.g., email"></b-form-input>
              </b-form-group>
              <b-form-group label="utm_campaign" label-size="sm" class="mb-2">
                <b-form-input v-model="customUtm.campaign" size="sm" placeholder="e.g., spring-2026"></b-form-input>
              </b-form-group>
              <b-form-group label="utm_content" label-size="sm" class="mb-2">
                <b-form-input v-model="customUtm.content" size="sm" placeholder="e.g., header-link"></b-form-input>
              </b-form-group>
              <div class="custom-url-preview p-2 bg-light rounded mb-2">
                <small class="text-muted text-break">{{ customUtmUrl }}</small>
              </div>
              <b-button
                size="sm"
                :variant="copiedType === 'custom' ? 'success' : 'primary'"
                @click="copyCustomUtm"
              >
                <b-icon :icon="copiedType === 'custom' ? 'check' : 'clipboard'"></b-icon>
                {{ $t('buttons.copyLink') }}
              </b-button>
            </div>
          </b-collapse>
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UtmShareSection',
  props: {
    publishingUrl: {
      type: String,
      required: true,
    },
    pageType: {
      type: String,
      default: 'page',
    },
    slug: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      campaignName: '',
      copiedType: null,
      copyTimeout: null,
      platforms: [
        {
          id: 'facebook',
          name: 'Facebook',
          icon: 'facebook',
          source: 'facebook',
          medium: 'social',
        },
        {
          id: 'twitter',
          name: 'Twitter/X',
          icon: 'twitter',
          source: 'twitter',
          medium: 'social',
        },
        {
          id: 'linkedin',
          name: 'LinkedIn',
          icon: 'linkedin',
          source: 'linkedin',
          medium: 'social',
        },
        {
          id: 'whatsapp',
          name: 'WhatsApp',
          icon: 'whatsapp',
          source: 'whatsapp',
          medium: 'messenger',
        },
        {
          id: 'email',
          name: 'Email',
          icon: 'envelope',
          source: 'email',
          medium: 'email',
        },
        {
          id: 'google_ads',
          name: 'Google Ads',
          icon: 'google',
          source: 'google',
          medium: 'cpc',
        },
      ],
      customUtm: {
        source: '',
        medium: '',
        campaign: '',
        content: '',
      },
    };
  },
  computed: {
    customUtmUrl() {
      return this.buildUtmUrl(this.customUtm);
    },
  },
  methods: {
    buildUtmUrl(params) {
      const url = new URL(this.publishingUrl);
      if (params.source) url.searchParams.set('utm_source', params.source);
      if (params.medium) url.searchParams.set('utm_medium', params.medium);
      if (params.campaign || this.campaignName) {
        url.searchParams.set('utm_campaign', params.campaign || this.campaignName);
      }
      if (params.content) url.searchParams.set('utm_content', params.content);
      return url.toString();
    },
    copyWithUtm(platform) {
      const utmUrl = this.buildUtmUrl({
        source: platform.source,
        medium: platform.medium,
        campaign: this.campaignName,
      });
      this.copyToClipboard(utmUrl, platform.id);
    },
    copyCustomUtm() {
      this.copyToClipboard(this.customUtmUrl, 'custom');
    },
    async copyToClipboard(text, type) {
      try {
        await navigator.clipboard.writeText(text);
        this.copiedType = type;

        // Clear previous timeout
        if (this.copyTimeout) {
          clearTimeout(this.copyTimeout);
        }

        // Reset after 2 seconds
        this.copyTimeout = setTimeout(() => {
          this.copiedType = null;
        }, 2000);

        // Show toast notification
        this.$bvToast.toast(this.$t('modals.showPublishSuccess.linkCopied'), {
          variant: 'success',
          solid: true,
          autoHideDelay: 2000,
          noCloseButton: true,
        });

        this.$emit('link-copied', { url: text, type });
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },
  },
  beforeDestroy() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
  },
};
</script>

<style scoped lang="scss">
.utm-sharing-content {
  width: 66.67%; // ~col-8
  max-width: 400px;
}
.campaign-input {
  margin-bottom: 0.5rem;

  ::v-deep label {
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
  }

  ::v-deep input {
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
    height: auto;
  }
}
.platform-buttons {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.4rem;
}
.platform-btn {
  font-size: 0.7rem;
  padding: 0.3rem 0.4rem;
  white-space: nowrap;

  .platform-name {
    font-size: 0.65rem;
  }
}
.custom-utm-form {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;

  ::v-deep label {
    font-size: 0.7rem;
    margin-bottom: 0.15rem;
  }

  ::v-deep input {
    font-size: 0.75rem;
    padding: 0.2rem 0.4rem;
    height: auto;
  }
}
.custom-url-preview {
  word-break: break-all;
  font-size: 0.7rem;
}
</style>
